import KineticHero from "@/components/KineticHero";
import ServiceRevealList from "@/components/ServiceRevealList";
import Manifesto from "@/components/Manifesto";
import CoverageGrid from "@/components/CoverageGrid";
import Marquee from "@/components/Marquee";
import useSEO from "@/hooks/useSEO";
import { withCanadaKeywords } from "@/lib/data/canada-keywords";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export default function Home() {
  useSEO({
    title:
      "Manitoba Security Services — Security Guard Services Across Manitoba",
    description:
      "Professional security guard services across every city in Manitoba. Mining security, mobile patrol, event security, corporate concierge, construction site protection, and more — one operating standard, province-wide.",
    keywords: withCanadaKeywords([
      "Manitoba security guard services",
      "security company Manitoba",
      "mining security Manitoba",
      "mobile patrol Winnipeg",
      "event security Manitoba",
      "corporate security Winnipeg",
      "construction site security",
      "retail loss prevention Manitoba",
      "residential security Winnipeg",
      "executive protection Manitoba",
      "fire watch Manitoba",
      "fire watch security Winnipeg",
      "hot work fire watch Manitoba",
      "sprinkler impairment fire watch",
      "24 hour fire watch service Manitoba",
    ]),
    canonical: typeof window !== "undefined" ? window.location.origin + "/" : "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": ["Organization", "LocalBusiness", "SecurityService"],
          "@id":
            (typeof window !== "undefined" ? window.location.origin : "") +
            "/#organization",
          name: "Manitoba Security Services",
          url: typeof window !== "undefined" ? window.location.origin : "",
          areaServed: {
            "@type": "AdministrativeArea",
            name: "Manitoba",
            containedInPlace: { "@type": "Country", name: "Canada" },
          },
          address: {
            "@type": "PostalAddress",
            addressRegion: "MB",
            addressCountry: "CA",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 49.8951,
            longitude: -97.1384,
          },
          priceRange: "$$",
          description:
            "Professional security guard services across every city in Manitoba — mining security, mobile patrol, event security, corporate concierge, construction site protection, retail loss prevention, executive protection, healthcare and industrial security.",
          serviceType: [
            "Mining Security",
            "Mobile Patrol",
            "Event Security",
            "Corporate Security",
            "Construction Site Security",
            "Retail Loss Prevention",
            "Residential Security",
            "Executive Protection",
            "Concierge Security",
            "Alarm Response",
            "Warehouse and Industrial Security",
            "Oil and Gas Site Security",
            "Healthcare Security",
            "Education Security",
            "Fire Watch Security",
          ],
          knowsAbout: [
            "Mining security",
            "Mobile patrol",
            "Event security",
            "Corporate security",
            "Construction site security",
            "Retail loss prevention",
            "Residential security",
            "Executive protection",
            "Concierge security",
            "Alarm response",
            "Warehouse and industrial security",
            "Oil and gas site security",
            "Healthcare security",
            "Education campus security",
            "Winnipeg security services",
            "Brandon security services",
            "Thompson mining security",
            "Flin Flon mining security",
          ],
        },
        {
          "@type": "WebSite",
          "@id":
            (typeof window !== "undefined" ? window.location.origin : "") +
            "/#website",
          url: typeof window !== "undefined" ? window.location.origin : "",
          name: "Manitoba Security Services",
          publisher: {
            "@id":
              (typeof window !== "undefined" ? window.location.origin : "") +
              "/#organization",
          },
          inLanguage: "en-CA",
        },
      ],
    },
  });

  return (
    <>
      <KineticHero />

      <Marquee
        items={[
          "VIGILANCE",
          "AUTHORITY",
          "DISCIPLINE",
          "MANITOBA",
          "STANDING GUARD",
        ]}
      />

      <ServiceRevealList />

      {/* Sector programs teaser */}
      <section
        data-testid="home-sectors"
        className="relative bg-[#050505] border-t border-[#1e1e1e] py-24 md:py-32"
      >
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-4">
            <p className="font-editorial italic text-[#FF3B30] text-sm">
              — Sector focus
            </p>
            <h2 className="mt-4 font-display font-black uppercase tracking-tightest leading-[0.9] text-white text-4xl md:text-5xl lg:text-6xl">
              Curated{" "}
              <span className="font-editorial italic font-normal text-[#a0a0a0]">
                programs
              </span>
              <br />
              for specific industries.
            </h2>
            <p className="mt-6 font-body text-base text-[#a0a0a0] max-w-md leading-relaxed">
              Twelve hand-built sector programs — mining in Thompson, oil &
              gas in Melita, event security in Winnipeg, and more.
            </p>
            <Link
              to="/sectors"
              data-testid="home-sectors-cta"
              className="inline-flex mt-8 items-center gap-2 font-display uppercase tracking-[0.16em] text-xs text-white border border-white/20 hover:border-[#FF3B30] px-5 py-3 transition-colors"
            >
              View sector programs →
            </Link>
          </div>
          <div className="col-span-12 md:col-span-8">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              {[
                "mining-security-thompson",
                "oil-gas-security-melita",
                "event-security-winnipeg",
                "corporate-security-winnipeg",
                "healthcare-security-winnipeg",
                "warehouse-security-winnipeg",
              ].map((s, i) => (
                <li key={s} className="border-b border-[#1e1e1e]">
                  <Link
                    to={`/sectors/${s}`}
                    data-testid={`home-sector-${s}`}
                    className="group flex items-baseline justify-between gap-4 py-4 hover:border-[#FF3B30] transition-colors"
                  >
                    <span className="font-display uppercase tracking-tight text-white text-base md:text-lg group-hover:translate-x-1 transition-transform">
                      <span className="font-editorial italic text-[#6a6a6a] mr-2">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {s.replace(/-/g, " ")}
                    </span>
                    <span className="font-editorial italic text-[#6a6a6a] text-xs">
                      →
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <Manifesto />

      <CoverageGrid />

      {/* Closing statement */}
      <section
        data-testid="home-cta"
        className="relative bg-[#050505] border-t border-[#1e1e1e] py-24 md:py-32"
      >
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-8 md:col-start-3 text-center">
            <p className="font-editorial italic text-[#FF3B30] text-sm mb-6">
              05 — Standard
            </p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-black uppercase tracking-tightest leading-[0.9] text-white text-4xl md:text-6xl lg:text-7xl mb-8"
            >
              We do not <span className="font-editorial italic font-normal text-[#a0a0a0]">chase</span> national contracts.
            </motion.h2>
            <p className="font-body text-base md:text-lg text-[#a0a0a0] max-w-2xl mx-auto leading-relaxed">
              Manitoba Security Services exists to serve Manitoba. That is the
              first sentence of our operating charter, and it dictates every
              staffing, training, and coverage decision we make.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/services"
                data-testid="cta-services"
                className="group inline-flex items-center gap-3 border border-white/20 hover:border-[#FF3B30] px-8 py-4 font-display uppercase tracking-[0.18em] text-sm text-white transition-colors"
              >
                Review Services
                <span className="group-hover:translate-x-1 transition-transform">
                  →
                </span>
              </Link>
              <Link
                to="/cities"
                data-testid="cta-coverage"
                className="group inline-flex items-center gap-3 border border-transparent hover:border-white/20 px-8 py-4 font-display uppercase tracking-[0.18em] text-sm text-white/80 hover:text-white transition-colors"
              >
                Coverage Map
                <span className="group-hover:translate-x-1 transition-transform">
                  →
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
